// CouponSection.tsx

import { useState } from "react";
import { Button, FloatingLabelInput } from "@/design-system/Atoms";
import { useCheckoutService } from "@/rtkQuery/features/hooks/useCheckoutService";

interface CouponSectionProps {
    productId: string | number | null;
    onCouponApplied?: (couponData: any) => void;
}

const CouponSection = ({ productId, onCouponApplied }: CouponSectionProps) => {
    const [couponCode, setCouponCode] = useState("");
    const {
        applyCoupon,
        couponLoading,
        couponSuccess,
        couponData,
        couponIsError,
        couponErrorMessage,
    } = useCheckoutService();

    const handleApplyCoupon = async () => {
        if (!couponCode || !productId) return;

        try {
            const result = await applyCoupon({ coupon_code: couponCode, productId: String(productId) }).unwrap();
            if (onCouponApplied && result?.Result === "Success") {
                onCouponApplied(result);
            }
        } catch (err) {
            console.error("Coupon apply failed:", err);
        }
    };

    return (
        <div className="form-bg bg-[#f6f7f9] mb-6 p-8 rounded-[20px] animate-fade-in">
            <h3 className="text-2xl font-normal mb-5 text-[#181a20]">
                Have a Coupon Code?
            </h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-8">
                    <FloatingLabelInput
                        id="couponCode"
                        label="Enter Coupon Code Here"
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="transition-all duration-200 focus:shadow-md"
                    />
                </div>
                <div className="lg:col-span-4">
                    <Button
                        variant="secondary"
                        size="xl"
                        fullWidth
                        onClick={handleApplyCoupon}
                        loading={couponLoading}
                        aria-label="Apply Coupon"
                        className="transition-all duration-200 hover:shadow-md"
                    >
                        Apply Coupon
                    </Button>
                </div>
            </div>

            {couponSuccess && couponData?.Result === "Success" && (
                <div className="animate-fade-in-up mt-6 p-4 border border-green-300 bg-green-50 rounded-md text-sm text-green-600">
                    Coupon applied: {couponData?.Object?.Coupon || couponCode}
                    {couponData?.Object?.Discount !== undefined && (
                        <span className="block mt-1">
                            Discount: ${couponData.Object.Discount.toFixed(2)}
                        </span>
                    )}
                </div>
            )}

            {couponIsError && couponErrorMessage && (
                <div className="animate-fade-in-up mt-6 p-4 border border-red-300 bg-red-50 rounded-md text-sm text-red-700">
                    {couponErrorMessage}
                </div>
            )}
        </div>
    );
};

export default CouponSection;